/**
 * <faq-tabs>
 * Pill tab switcher for the FAQ section. Follows the WAI-ARIA tabs pattern:
 * click or Arrow/Home/End keys move between tabs, one panel visible at a time.
 * Accordion rows inside each panel are native <details>, no JS needed.
 */
class FaqTabs extends HTMLElement {
  connectedCallback() {
    this.tablist = this.querySelector('[role="tablist"]');
    this.tabs = [...this.querySelectorAll('[data-faq-tab]')];
    this.panels = [...this.querySelectorAll('[data-faq-panel]')];
    if (!this.tablist || this.tabs.length < 2) return;

    this.handleClick = this.handleClick.bind(this);
    this.handleKeydown = this.handleKeydown.bind(this);
    this.handleBlockSelect = this.handleBlockSelect.bind(this);

    this.tablist.addEventListener('click', this.handleClick);
    this.tablist.addEventListener('keydown', this.handleKeydown);
    document.addEventListener('shopify:block:select', this.handleBlockSelect);
  }

  disconnectedCallback() {
    this.tablist?.removeEventListener('click', this.handleClick);
    this.tablist?.removeEventListener('keydown', this.handleKeydown);
    document.removeEventListener('shopify:block:select', this.handleBlockSelect);
  }

  handleClick(event) {
    const tab = event.target.closest('[data-faq-tab]');
    if (!tab) return;
    this.activate(tab);
  }

  handleKeydown(event) {
    const current = event.target.closest('[data-faq-tab]');
    if (!current) return;

    const index = this.tabs.indexOf(current);
    const last = this.tabs.length - 1;
    let next = null;

    switch (event.key) {
      case 'ArrowRight':
        next = index === last ? 0 : index + 1;
        break;
      case 'ArrowLeft':
        next = index === 0 ? last : index - 1;
        break;
      case 'Home':
        next = 0;
        break;
      case 'End':
        next = last;
        break;
      default:
        return;
    }

    event.preventDefault();
    const tab = this.tabs[next];
    this.activate(tab);
    tab.focus();
  }

  /** Theme editor: selecting a tab or question block reveals its panel. */
  handleBlockSelect(event) {
    const block = event.target;
    if (!this.contains(block)) return;

    const tab = block.closest('[data-faq-tab]');
    if (tab) {
      this.activate(tab);
      return;
    }

    const panel = block.closest('[data-faq-panel]');
    if (!panel) return;
    const owner = this.tabs.find((candidate) => candidate.getAttribute('aria-controls') === panel.id);
    if (owner) this.activate(owner);
  }

  activate(tab) {
    const targetId = tab.getAttribute('aria-controls');

    for (const candidate of this.tabs) {
      const isActive = candidate === tab;
      candidate.classList.toggle('is-active', isActive);
      candidate.setAttribute('aria-selected', String(isActive));
      candidate.setAttribute('tabindex', isActive ? '0' : '-1');
    }

    for (const panel of this.panels) {
      const isActive = panel.id === targetId;
      panel.classList.toggle('is-active', isActive);
      panel.toggleAttribute('hidden', !isActive);
    }

    this.dispatchEvent(
      new CustomEvent('theme:faq-tabs:changed', {
        detail: { tabId: tab.id, panelId: targetId },
        bubbles: true,
      })
    );
  }
}

if (!customElements.get('faq-tabs')) {
  customElements.define('faq-tabs', FaqTabs);
}
